// ═══════════════════════════════════════════════════════════
// Delta-sync merge helper (Phase 2)
//
// Applies a list of delta items to an existing state list:
//   • items with `deleted: true` → remove by id (tombstone)
//   • all other items            → upsert by id (replace if exists,
//                                                append if new)
//
// Order preservation:
//   - Existing items stay in their original order, with mutations
//     applied in place.
//   - Newly-added items (in delta but not in current state) are
//     appended at the end. Consumer-side sorts (Calendar, OrdersPage)
//     re-order downstream — order here is a stable starting point.
//
// Pure function. No side effects. Safe to unit-test in isolation.
// ═══════════════════════════════════════════════════════════

// Normalise ids to string for matching. Mixed-type ids slip in via two
// paths: (1) optimistic local append from a save handler that uses
// Date.now() (Number) while the server-side refetch returns SQL TEXT
// columns (String); (2) legacy Firestore docs that stored ids as
// numbers. Without this, `Map.has(Number) === Map.has(String)` is false
// and the dedupe loop fails → the same record appears twice in the
// merged list until the page reloads.
const idKey = (v) => (v == null ? '' : String(v));

export function mergeDelta(currentList, deltaItems) {
  if (!deltaItems || deltaItems.length === 0) return currentList;
  const tombstoneIds = new Set();
  const updates = new Map();
  for (const item of deltaItems) {
    if (item.deleted) tombstoneIds.add(idKey(item.id));
    else updates.set(idKey(item.id), item);
  }
  const seen = new Set();
  const merged = [];
  for (const item of currentList) {
    const key = idKey(item.id);
    if (tombstoneIds.has(key)) continue;
    if (updates.has(key)) {
      merged.push(updates.get(key));
      seen.add(key);
    } else {
      merged.push(item);
    }
  }
  // Append new (non-existing) items. A tombstone for the same id wins
  // even if an update also appeared in the same delta — server can emit
  // both inside the 5s overlap window, but the latest state is "deleted".
  for (const [key, item] of updates) {
    if (seen.has(key)) continue;
    if (tombstoneIds.has(key)) continue;
    merged.push(item);
  }
  return merged;
}
